import { useState } from "react";
import { Check, Copy, Zap, ExternalLink, Send, Inbox } from "lucide-react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { apiRequest, queryClient } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";

type WebhookInfo = {
  readonly url: string;
  readonly secret: string | null;
  readonly ownerUserId: string;
  readonly supportedEvents: ReadonlyArray<{ event: string; purpose: string }>;
};

type RecentEvent = {
  readonly receivedAt: string | null;
  readonly ghlEvent: string | null;
  readonly eventName: string;
  readonly email: string | null;
  readonly phone: string | null;
  readonly name: string | null;
  readonly revenue: string | null;
  readonly currency: string | null;
  readonly status: number | null;
  readonly ok: boolean;
  readonly error: string | null;
  readonly isTest: boolean;
};

type RecentEventsResponse = { readonly events: ReadonlyArray<RecentEvent> };

const RECENT_EVENTS_KEY = "/api/integrations/crm/ghl/recent-events";

function formatReceivedAt(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "—";
  return d.toLocaleString("pl-PL", {
    day: "2-digit",
    month: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
}

function formatRevenue(revenue: string | null, currency: string | null): string {
  if (revenue == null) return "—";
  const num = Number(revenue);
  if (Number.isNaN(num)) return "—";
  return `${num.toLocaleString("pl-PL")} ${currency ?? ""}`.trim();
}

function CopyField({ label, value }: { label: string; value: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // noop
    }
  };

  return (
    <div className="space-y-1">
      <div className="text-[11px] uppercase tracking-widest text-white/40">{label}</div>
      <div className="flex items-center gap-2 rounded-md border border-white/10 bg-white/[0.04] p-2">
        <code className="flex-1 truncate font-mono text-xs text-white/90">{value}</code>
        <button
          onClick={handleCopy}
          type="button"
          className="shrink-0 rounded-md p-1 text-white/50 hover:text-white hover:bg-white/[0.06] transition-colors"
          title="Skopiuj"
        >
          {copied ? (
            <Check className="h-3.5 w-3.5 text-emerald-300" />
          ) : (
            <Copy className="h-3.5 w-3.5" />
          )}
        </button>
      </div>
    </div>
  );
}

export function GhlWebhookSetupPanel() {
  const [expanded, setExpanded] = useState(false);
  const { toast } = useToast();

  const { data, isLoading } = useQuery<WebhookInfo>({
    queryKey: ["/api/integrations/crm/webhook-info"],
    queryFn: () => apiRequest("GET", "/api/integrations/crm/webhook-info"),
    staleTime: 5 * 60 * 1000,
    enabled: expanded, // dont fetch until user opens panel
  });

  const { data: recent } = useQuery<RecentEventsResponse>({
    queryKey: [RECENT_EVENTS_KEY],
    queryFn: () => apiRequest("GET", RECENT_EVENTS_KEY),
    staleTime: 30 * 1000,
    enabled: expanded,
  });

  const testMutation = useMutation({
    mutationFn: () => apiRequest("POST", "/api/integrations/crm/ghl/test"),
    onSuccess: () => {
      toast({
        title: "Webhook testowy wyslany",
        description: 'Sprawdz "Ostatnie zdarzenia z GHL" ponizej.',
      });
      queryClient.invalidateQueries({ queryKey: [RECENT_EVENTS_KEY] });
    },
    onError: () => {
      toast({
        title: "Test nieudany",
        description: "Nie udalo sie wyslac testowego zdarzenia.",
        variant: "destructive",
      });
    },
  });

  const recentEvents = recent?.events ?? [];

  return (
    <div className="mt-4 rounded-lg border border-white/10 bg-white/[0.02] p-4 space-y-3">
      <button onClick={() => setExpanded((v) => !v)} className="w-full text-left" type="button">
        <div className="flex items-start justify-between gap-3">
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold text-white flex items-center gap-2">
              <Zap className="h-4 w-4 text-sky-300" />
              Real-time tracking (webhook GHL → nasza apka)
            </div>
            <p className="text-xs text-white/60 mt-1">
              Zamiast czekac na polling co 5 min, GHL Workflow moze pchnac event do nas natychmiast
              po zdarzeniu. sprzedaz/rozmowa pojawia sie w dashboardzie w sekunde.{" "}
              <span className="text-sky-300/80">Opcjonalne, ale daje real-time ROAS.</span>
            </p>
          </div>
          <span className="shrink-0 text-[11px] uppercase tracking-widest text-white/40">
            {expanded ? "Zwin" : "Konfiguruj"}
          </span>
        </div>
      </button>

      {expanded && (
        <div className="space-y-4 pt-2 border-t border-white/[0.06]">
          {isLoading && <p className="text-xs text-white/40">Wczytywanie...</p>}

          {data && (
            <>
              <div className="space-y-3">
                <CopyField label="URL webhooka" value={data.url} />
                <div className="rounded-md border border-sky-400/20 bg-sky-400/[0.05] p-2.5 space-y-1">
                  <p className="text-xs text-sky-100/90">
                    <strong>Autoryzacja:</strong> w naglowku{" "}
                    <code className="font-mono">X-Webhook-Secret</code> wklej swoj{" "}
                    <strong>API key</strong> (<code className="font-mono">gfy_...</code>).
                  </p>
                  <p className="text-[11px] text-white/55 leading-relaxed">
                    Klucz wygenerujesz w <strong>Ustawienia → API Keys</strong>. Klucz identyfikuje
                    Twoje konto, wiec <code className="font-mono">owner_user_id</code> w body jest{" "}
                    <strong>opcjonalny</strong>.
                  </p>
                </div>
                {data.secret && (
                  <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-2.5">
                    <p className="text-[11px] text-white/50 leading-relaxed">
                      <strong className="text-white/70">Fallback:</strong> na serwerze ustawiony
                      jest tez globalny secret (legacy). Mozesz go uzyc zamiast API key, ale
                      rekomendowany jest klucz per-user.
                    </p>
                  </div>
                )}
                <CopyField
                  label="Twoj user ID (opcjonalny owner_user_id w body)"
                  value={data.ownerUserId}
                />
              </div>

              <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-3 space-y-2">
                <div className="text-[11px] uppercase tracking-widest text-white/40">
                  Eventy ktore obslugujemy
                </div>
                <ul className="space-y-1.5">
                  {data.supportedEvents.map((ev) => (
                    <li key={ev.event} className="flex flex-col gap-0.5 text-xs">
                      <code className="font-mono text-emerald-300">{ev.event}</code>
                      <span className="text-white/60 leading-relaxed">{ev.purpose}</span>
                    </li>
                  ))}
                </ul>
              </div>

              <div className="rounded-md border border-sky-400/20 bg-sky-400/[0.05] p-3 space-y-2">
                <div className="text-sm font-semibold text-sky-200 flex items-center gap-1.5">
                  <ExternalLink className="h-3.5 w-3.5" />
                  Jak skonfigurowac (3 kroki)
                </div>
                <ol className="space-y-2 text-xs text-white/75 leading-relaxed list-decimal list-inside">
                  <li>
                    Wygeneruj <strong>API key</strong> w <strong>Ustawienia → API Keys</strong>{" "}
                    (zaczyna sie od <code className="font-mono">gfy_</code>).
                  </li>
                  <li>
                    W GHL otworz <strong>Automation → Workflows</strong>, stworz/edytuj workflow,
                    dodaj <strong>Trigger</strong> (np. "Opportunity Status Changed → Won") +{" "}
                    <strong>Action: Webhook</strong>.
                  </li>
                  <li>
                    W Action Webhook wklej <strong>URL</strong>, ustaw{" "}
                    <strong>Method = POST</strong>, w <strong>Headers</strong> dodaj{" "}
                    <code className="font-mono">X-Webhook-Secret</code> = Twoj API key (
                    <code className="font-mono">gfy_...</code>), w <strong>Body</strong> wklej JSON:
                    <pre className="mt-1.5 rounded bg-black/40 p-2 overflow-x-auto font-mono text-[11px] text-white/80">{`{
  "event": "opportunity.won",
  "email": "{{contact.email}}",
  "phone": "{{contact.phone}}",
  "name": "{{contact.name}}",
  "contact_id": "{{contact.id}}",
  "revenue": "{{opportunity.lead_value}}",
  "order_id": "{{opportunity.id}}"
}`}</pre>
                    <code className="font-mono">owner_user_id</code> jest opcjonalny — API key juz
                    identyfikuje Twoje konto.
                  </li>
                </ol>
                <p className="text-[11px] text-white/50 leading-relaxed pt-1 border-t border-white/[0.06]">
                  <strong className="text-white/70">Tip:</strong> stworz osobny workflow dla kazdego
                  eventu (won, lost, appointment.booked, tag_added). Pelna dokumentacja
                  krok-po-kroku ze screenshotami:{" "}
                  <code className="font-mono">docs/ghl-webhook-setup.md</code>
                </p>
              </div>

              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => testMutation.mutate()}
                  disabled={testMutation.isPending}
                  className="inline-flex items-center gap-1.5 rounded-md border border-emerald-400/30 bg-emerald-400/[0.08] px-3 py-1.5 text-xs font-medium text-emerald-100 hover:bg-emerald-400/[0.14] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  <Send className="h-3.5 w-3.5" />
                  {testMutation.isPending ? "Wysylanie..." : "Testuj webhook"}
                </button>
                <span className="text-[11px] text-white/45">
                  Wysyla testowy event "purchase" przez pelny pipeline.
                </span>
              </div>

              <div className="rounded-md border border-white/[0.06] bg-white/[0.02] p-3 space-y-2.5">
                <div className="text-[11px] uppercase tracking-widest text-white/40 flex items-center gap-1.5">
                  <Inbox className="h-3.5 w-3.5" />
                  Ostatnie zdarzenia z GHL
                </div>
                {recentEvents.length === 0 ? (
                  <p className="text-xs text-white/45 py-2">
                    Brak zdarzen. Wyslij <strong>Test Workflow</strong> z GHL (albo „Testuj webhook"
                    wyzej) — kazde przyjscie pojawi sie tu, takze odrzucone (ze statusem i powodem).
                  </p>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="text-left text-white/40 border-b border-white/[0.06]">
                          <th className="py-1.5 pr-3 font-medium">Status</th>
                          <th className="py-1.5 pr-3 font-medium">Czas</th>
                          <th className="py-1.5 pr-3 font-medium">Event</th>
                          <th className="py-1.5 pr-3 font-medium">Email</th>
                          <th className="py-1.5 pr-3 font-medium">Przychod</th>
                          <th className="py-1.5 font-medium"></th>
                        </tr>
                      </thead>
                      <tbody>
                        {recentEvents.map((ev, i) => (
                          <tr key={i} className="border-b border-white/[0.03] last:border-0">
                            <td className="py-1.5 pr-3 whitespace-nowrap">
                              {ev.ok ? (
                                <span
                                  className="inline-flex items-center gap-1 rounded bg-emerald-400/[0.12] px-1.5 py-0.5 text-[10px] font-medium text-emerald-300"
                                  title={`${ev.status ?? 200} OK`}
                                >
                                  <Check className="h-3 w-3" />
                                  OK
                                </span>
                              ) : (
                                <span
                                  className="inline-flex items-center gap-1 rounded bg-red-400/[0.12] px-1.5 py-0.5 text-[10px] font-medium text-red-300"
                                  title={ev.error ?? `HTTP ${ev.status ?? ""}`}
                                >
                                  ✗ {ev.status ?? "err"}
                                </span>
                              )}
                            </td>
                            <td className="py-1.5 pr-3 text-white/60 whitespace-nowrap">
                              {formatReceivedAt(ev.receivedAt)}
                            </td>
                            <td className="py-1.5 pr-3">
                              <code
                                className={`font-mono ${ev.ok ? "text-emerald-300" : "text-white/50"}`}
                              >
                                {ev.ghlEvent ?? ev.eventName ?? "—"}
                              </code>
                            </td>
                            <td className="py-1.5 pr-3 text-white/70 truncate max-w-[160px]">
                              {ev.email ?? ev.phone ?? "—"}
                            </td>
                            <td className="py-1.5 pr-3 text-white/70 whitespace-nowrap">
                              {formatRevenue(ev.revenue, ev.currency)}
                            </td>
                            <td className="py-1.5">
                              {ev.isTest && (
                                <span className="inline-flex rounded bg-amber-400/[0.12] px-1.5 py-0.5 text-[10px] font-medium text-amber-200">
                                  test
                                </span>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {recentEvents.some((e) => !e.ok) && (
                      <p className="mt-1.5 text-[11px] text-red-300/70">
                        Czerwone = webhook dotarl, ale zostal odrzucony — najedz na status, zeby
                        zobaczyc powod (np. brak email/phone albo zly JSON).
                      </p>
                    )}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}
